/**
 * 그림퀴즈 태블릿 러너.
 * 첫 진입은 전체 문항 중 하나를 무작위로 고르고, 오답이면 이번 수행에서 아직 출제하지
 * 않은 문항 중 하나를 다시 고른다. 전체 문항을 한 번씩 풀면 정답이 없어도 수행을
 * 종료한다. 정·오답 연출은 같은 세트의 OX 공통 영상을 재사용한다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useQuestVideoWatchdog from '../../hooks/useQuestVideoWatchdog';
import PictureQuizVerdictOverlay from './PictureQuizVerdictOverlay';
import oxDoneCharacter from '../../assets/quest/ox_done_char.webp';
import './PictureQuizRunner.css';

export const PICTURE_QUIZ_RESULT_MS = 1800;

export function pickPictureQuizQuestion(questions, randomValue = Math.random()) {
  if (!Array.isArray(questions) || questions.length === 0) return null;
  const value = Number(randomValue);
  const index = Number.isFinite(value)
    ? Math.max(0, Math.min(questions.length - 1, Math.floor(value * questions.length)))
    : 0;
  return questions[index];
}

export function pickNextPictureQuizQuestion(
  questions, answeredQids, randomValue = Math.random(),
) {
  const answered = new Set(Array.isArray(answeredQids) ? answeredQids : []);
  const remaining = Array.isArray(questions)
    ? questions.filter((question) => question && !answered.has(question.qid))
    : [];
  return pickPictureQuizQuestion(remaining, randomValue);
}

export function shouldCompletePictureQuizRun(isCorrect, questions, answers) {
  if (isCorrect) return true;
  if (!Array.isArray(questions) || questions.length === 0) return false;

  const questionQids = new Set(
    questions.filter(Boolean).map((item) => item.qid),
  );
  const answeredQids = new Set(
    (Array.isArray(answers) ? answers : []).filter(Boolean).map((item) => item.qid),
  );
  return questionQids.size > 0
    && [...questionQids].every((qid) => answeredQids.has(qid));
}

export function resolvePictureQuizVerdictVideoUrl(config, sharedVerdictConfig, isCorrect) {
  // 그림퀴즈에서 재사용하는 X 결과 영상 두 개는 현재 API 필드명과 실제 영상 내용이 반대다.
  // X 전용 키만 교차 연결하고, 일반/O 결과 영상의 의미는 그대로 보존한다.
  const keys = isCorrect
    ? ['x_incorrect_video_url', 'correct_video_url', 'o_correct_video_url']
    : ['x_correct_video_url', 'incorrect_video_url', 'o_incorrect_video_url'];
  for (const source of [config, sharedVerdictConfig]) {
    if (!source || typeof source !== 'object') continue;
    for (const key of keys) {
      const url = source[key];
      if (typeof url === 'string' && url.trim()) return url.trim();
    }
  }
  return null;
}

export function resolvePictureChoiceKey(event) {
  if (!event || event.repeat || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) {
    return null;
  }
  const raw = event.key ?? event.code;
  const match = String(raw || '').match(/^(?:Digit|Numpad)?([123])$/);
  return match ? Number(match[1]) : null;
}

export function resolvePictureQuizCharacterUrl(question) {
  if (!question || typeof question !== 'object') return null;
  const candidates = [
    question.character_url,
    question.character_image_url,
    question.character?.image_url,
  ];
  const url = candidates.find((candidate) => (
    typeof candidate === 'string' && candidate.trim()
  ));
  return url ? url.trim() : null;
}

export function normalizePictureQuizQuestion(question) {
  if (!question || typeof question !== 'object') return null;
  const rawText = question.question_text ?? question.title ?? question.text;
  const text = typeof rawText === 'string' ? rawText.trim() : '';
  const choices = Array.isArray(question.choices)
    ? question.choices.map((choice, index) => {
      const source = choice && typeof choice === 'object' ? choice : {};
      const mediaUrl = source.video_url ?? source.image_url ?? source.character_url;
      const rawChoiceText = source.choice_text ?? source.text ?? source.label;
      return {
        number: index + 1,
        mediaType: source.video_url
          ? 'video'
          : (source.image_url || source.character_url) ? 'image' : null,
        mediaUrl: typeof mediaUrl === 'string' && mediaUrl.trim() ? mediaUrl.trim() : null,
        text: typeof rawChoiceText === 'string' && rawChoiceText.trim()
          ? rawChoiceText.trim()
          : `${index + 1}번 보기`,
      };
    })
    : [];
  const questionVideoUrl = typeof question.question_video_url === 'string'
    && question.question_video_url.trim()
    ? question.question_video_url.trim()
    : null;
  const characterUrl = resolvePictureQuizCharacterUrl(question);
  return {
    qid: question.qid,
    text,
    answer: Number(question.answer),
    choices,
    questionVideoUrl,
    characterUrl,
  };
}

function PictureQuizQuestionVideo({ question, onFinished, onAbort }) {
  const { videoRef, videoPlaying, isVideoPhase, videoHandlers } = useQuestVideoWatchdog({
    active: true,
    videoUrl: question.questionVideoUrl,
    onFinished,
    onAbort,
    logLabel: '그림 퀴즈 문제 영상',
  });

  if (!isVideoPhase) return null;
  return (
    <section className="picture-quiz picture-quiz--question" data-testid="picture-quiz-question-video">
      <header className="picture-quiz__question-banner">
        <h1>{question.text}</h1>
      </header>
      <div className="picture-quiz__question-stage">
        <video
          ref={videoRef}
          className={`picture-quiz__question-video${videoPlaying ? ' is-playing' : ''}`}
          src={question.questionVideoUrl}
          autoPlay
          playsInline
          preload="auto"
          aria-label="그림 퀴즈 문제 영상"
          {...videoHandlers}
        />
      </div>
    </section>
  );
}

export default function PictureQuizRunner({
  config, sharedVerdictConfig, onComplete, onCancel, onVideoAbort,
}) {
  const questions = useMemo(
    () => (Array.isArray(config?.questions) ? config.questions : [])
      .map(normalizePictureQuizQuestion)
      .filter(Boolean),
    [config],
  );
  const [question, setQuestion] = useState(() => pickPictureQuizQuestion(questions));
  const [phase, setPhase] = useState(() => (question?.questionVideoUrl ? 'question' : 'choices'));
  const [selected, setSelected] = useState(null);
  const [verdictVideoUrl, setVerdictVideoUrl] = useState(null);
  const answersRef = useRef([]);
  const lastCorrectRef = useRef(false);
  const lockedRef = useRef(false);
  const timerRef = useRef(null);
  const [videoPlayingKeys, setVideoPlayingKeys] = useState(new Set());

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const advanceAfterVerdict = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setVerdictVideoUrl(null);

    if (shouldCompletePictureQuizRun(
      lastCorrectRef.current, questions, answersRef.current,
    )) {
      onComplete?.({ answers: answersRef.current });
      return;
    }

    const next = pickNextPictureQuizQuestion(
      questions,
      answersRef.current.map((answer) => answer.qid),
    );
    if (!next) {
      // 설정 중복 등으로 남은 문항을 찾지 못해도 재순환하지 않는다.
      onComplete?.({ answers: answersRef.current });
      return;
    }

    lockedRef.current = false;
    setSelected(null);
    setQuestion(next);
    setPhase(next?.questionVideoUrl ? 'question' : 'choices');
  }, [onComplete, questions]);

  const choose = useCallback((choiceNumber) => {
    if (lockedRef.current || !question || ![1, 2, 3].includes(choiceNumber)) return;
    lockedRef.current = true;
    const isCorrect = choiceNumber === question.answer;
    answersRef.current = [...answersRef.current, { qid: question.qid, chosen: choiceNumber }];
    lastCorrectRef.current = isCorrect;
    setSelected(choiceNumber);
    const videoUrl = resolvePictureQuizVerdictVideoUrl(
      config, sharedVerdictConfig, isCorrect,
    );
    if (videoUrl) {
      setVerdictVideoUrl(videoUrl);
      return;
    }

    setPhase(isCorrect ? 'correct' : 'wrong');
    timerRef.current = setTimeout(advanceAfterVerdict, PICTURE_QUIZ_RESULT_MS);
  }, [advanceAfterVerdict, config, question, sharedVerdictConfig]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (phase !== 'choices') return;
      const choice = resolvePictureChoiceKey(event);
      if (choice) {
        event.preventDefault();
        choose(choice);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [choose, phase]);

  if (!question || !question.text || question.choices.length !== 3
      || ![1, 2, 3].includes(question.answer)) {
    return (
      <section className="picture-quiz-state" role="alert">
        <strong>영상 선택 퀴즈 설정을 확인해 주세요.</strong>
        <button type="button" onClick={onCancel}>돌아가기</button>
      </section>
    );
  }

  // 피그마 흐름대로 보기 화면을 남긴 오버레이가 아니라 정답/오답 전용 다음 화면으로 전환한다.
  // 보기 영상 3개가 함께 언마운트되므로 태블릿이 결과 영상과 동시에 디코딩하지 않는다.
  if (verdictVideoUrl) {
    return (
      <PictureQuizVerdictOverlay
        videoUrl={verdictVideoUrl}
        onFinished={advanceAfterVerdict}
        onAbort={onVideoAbort}
      />
    );
  }

  if (phase === 'question') {
    return (
      <PictureQuizQuestionVideo
        question={question}
        onFinished={() => setPhase('choices')}
        onAbort={onVideoAbort}
      />
    );
  }

  if (phase === 'correct' || phase === 'wrong') {
    const correct = phase === 'correct';
    return (
      <section
        className={`picture-quiz-verdict picture-quiz-verdict--${phase}`}
        data-testid={`picture-quiz-${phase}`}
      >
        <img src={oxDoneCharacter} alt="" />
        <strong>{correct ? '정답이에요!' : '다시 한번 골라볼까요?'}</strong>
      </section>
    );
  }

  return (
    <section className="picture-quiz picture-quiz--choices" data-testid="picture-quiz-runner">
      <header className={`picture-quiz__question-banner${question.characterUrl ? ' picture-quiz__question-banner--with-character' : ''}`}>
        {question.characterUrl && (
          <img
            src={question.characterUrl}
            alt=""
            className="picture-quiz__question-icon"
            draggable="false"
          />
        )}
        <h1>{question.text}</h1>
      </header>

      <div className="picture-quiz__choices">
        {question.choices.map((choice) => (
          <button
            type="button"
            key={`${question.qid}-${choice.number}`}
            className="picture-quiz__choice"
            onClick={() => choose(choice.number)}
            disabled={selected != null}
            aria-label={`${choice.number}번 보기: ${choice.text}`}
            data-testid={`picture-quiz-choice-${choice.number}`}
          >
            {choice.mediaType === 'video' && (
              <video
                className={`picture-quiz__choice-video${videoPlayingKeys.has(`${question.qid}-${choice.number}`) ? ' picture-quiz__choice-video-playing' : ''}`}
                data-testid={`picture-quiz-choice-${choice.number}-video`}
                src={choice.mediaUrl}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                onPlaying={() => setVideoPlayingKeys((previous) => (
                  new Set(previous).add(`${question.qid}-${choice.number}`)
                ))}
                aria-label={`${choice.number}번 보기 영상`}
              />
            )}
            {choice.mediaType === 'image' && (
              <img
                className="picture-quiz__choice-legacy-image"
                data-testid={`picture-quiz-choice-${choice.number}-legacy-image`}
                src={choice.mediaUrl}
                alt=""
              />
            )}
            {!choice.mediaUrl && (
              <span className="picture-quiz__missing">영상을 불러올 수 없어요</span>
            )}
            <span
              className="picture-quiz__choice-text"
              data-testid={`picture-quiz-choice-${choice.number}-text`}
            >
              {choice.text}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
